import axios from "axios";

class AnalyticsIntegration {
  /**
   * Fetch tenant analytics data
   * @param {string} tenantId - The tenant ID
   * @returns {Promise} Analytics data response
   */
  /**
   * Fetch tenant users
   * @param {string} tenantId - The tenant ID
   * @returns {Promise} Users data response
   */
  async getTenantUsers(tenantId, payload = {}) {
    try {
      const requestBody = { ...payload };

      if (!requestBody.tenant_id) {
        requestBody.tenant_id = tenantId;
      }

      const response = await axios.post(`/tenants/${tenantId}/users-list`, requestBody);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantBranches(tenantId) {
    try {
      const response = await axios.post(`/tenants/${tenantId}/branches`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch tenant activity log
   * @param {string} tenantId - The tenant ID
   * @param {object} filters - Filter parameters (date range)
   * @returns {Promise} Activity log response
   */
  async getTenantActivityLog(tenantId, filters = {}) {
    try {
      let url = `/tenants/${tenantId}/user-activities`;

      const params = new URLSearchParams();
      const activeFilter = filters.dateFilter || {};
      const range = activeFilter.range || {};

      if (range.from) {
        params.append("from_date", range.from);
      }
      if (range.to) {
        params.append("to_date", range.to);
      }

      if (params.toString()) {
        url += "?" + params.toString();
      }

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch tenant feature usage data
   * @param {string} tenantId - The tenant ID
   * @param {object} filters - Filter parameters (date range, user, branch)
   * @returns {Promise} Feature usage data response
   */
  async getTenantFeatureUsageData(tenantId, filters = {}) {
    try {
      let url = `/tenants/${tenantId}/feature-usage-data`;

      const params = new URLSearchParams();
      const activeFilter = filters.dateFilter || {};
      const range = activeFilter.range || {};

      if (range.from) {
        params.append("from_date", range.from);
      }
      if (range.to) {
        params.append("to_date", range.to);
      }

      const appendFilterParam = (key, rawValue) => {
        if (rawValue === null || rawValue === undefined) {
          return;
        }

        const normalizeValue = value => {
          if (value === null || value === undefined || value === "") {
            return null;
          }
          if (typeof value === "object") {
            return value.id ?? value.value ?? value.key ?? null;
          }
          return value;
        };

        if (Array.isArray(rawValue)) {
          const normalizedList = rawValue
            .map(normalizeValue)
            .filter((value) => value !== null && value !== "all");

          if (normalizedList.length > 0) {
            params.append(key, normalizedList.join(","));
          }
          return;
        }

        const singleValue = normalizeValue(rawValue);
        if (singleValue === null || singleValue === "all") {
          return;
        }

        params.append(key, singleValue);
      };

      appendFilterParam("user_id", filters.user);
      appendFilterParam("branch_id", filters.branch);

      if (params.toString()) {
        url += "?" + params.toString();
      }

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantBasicInfo(tenantId) {
    try {
      const response = await axios.get(`/tenants/${tenantId}/basic-info`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantIntegrations(tenantId) {
    try {
      const response = await axios.post(
        `/tenants/${tenantId}/integration-list`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async changeTenantSubscriptionId(tenantId, payload) {
    try {
      const response = await axios.post(
        `/tenants/${tenantId}/change-subscription-id`,
        payload
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async toggleTenantSubscriptionStatus(tenantId) {
    try {
      const response = await axios.post(
        `/tenants/${tenantId}/set-inactive-status`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantSmsPhoneNumbers(tenantId) {
    try {
      const response = await axios.get(`/sms/phone-numbers`, {
        params: { tenant_id: tenantId }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async addSmsCredit(tenantId, payload) {
    try {
      const body = {
        tenant_id: tenantId,
        ...payload
      };
      const response = await axios.post(`/sms/add-credit`, body);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantWhatsappConfig(tenantId) {
    try {
      const response = await axios.get(`/tenants/${tenantId}/whatsapp-config`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantConfig(tenantId) {
    try {
      const response = await axios.get(`/tenants/${tenantId}/config`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantPhoneNumbers(tenantId) {
    try {
      const response = await axios.get(`/tenants/${tenantId}/phone-numbers`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantPhoneNumberLogs(tenantId) {
    try {
      const response = await axios.get(
        `/tenants/${tenantId}/phone-number-logs`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTenantPhoneNumberLogDetails(tenantId, phoneNumberCode) {
    try {
      const response = await axios.get(
        `/tenants/${tenantId}/phone-number-logs`,
        {
          params: {
            phone_number_code: phoneNumberCode
          }
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getSmsCreditLogs(tenantId, phoneNumberCode) {
    try {
      const response = await axios.get(`/sms/credit-logs`, {
        params: {
          tenant_id: tenantId,
          phone_number_code: phoneNumberCode
        }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async clearTenantCache(tenantId) {
    try {
      const response = await axios.get(`/tenants/${tenantId}/cache-clear`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updatePotentialUsers(tenantId, potentialUsers) {
    try {
      const response = await axios.post(
        `/tenants/${tenantId}/update-potential-users`,
        { potential_users: potentialUsers }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateTenantBannerStatus(tenantId, status) {
    try {
      const response = await axios.put(`/tenants/${tenantId}/${status}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default new AnalyticsIntegration();
